import { MODAL_PARAM } from '@members/modal/urls';
import { CLUB_PELOTON_MODAL_NAME } from './constants';

/**
 * sessionStorage key used to persist a pending Club Peloton modal deep-link
 * across the Auth0 login round-trip.
 *
 * WHY THIS EXISTS
 * ---------------
 * The modal open/closed state is driven entirely by the `?modal=clubPeloton`
 * query param (see ClubPelotonScoreCard). Marketing emails deep-link straight
 * to `/{locale}/profile/overview/?modal=clubPeloton`.
 *
 * When a *logged-out* member opens that link, the app performs a multi-hop
 * redirect before landing back on the overview page:
 *
 *   /{locale}/profile/overview/?modal=clubPeloton   (deep link, unauthenticated)
 *     → Auth0 universal login (redirect_uri = <origin>/callback, no query)
 *     → /callback?code=…&state=…                    (query dropped by Auth0)
 *     → location.assign(appState.returnTo)          (returnTo restores the URL)
 *
 * The `?modal=clubPeloton` param is only preserved if it survives every hop of
 * that chain intact. QA observed it surviving for some locales but not others
 * (see #club-peloton-team) — a fragile, environment-dependent outcome because
 * the param has to round-trip through Auth0 appState and a hard navigation.
 *
 * Rather than depend on the query param surviving the chain, we persist the
 * deep-link *intent* in sessionStorage the moment the logged-out member hits
 * the link (before the login redirect), then restore it once they land back on
 * the overview page authenticated. This makes the deep link behave identically
 * for every locale regardless of how the redirect chain handles the query.
 *
 * sessionStorage (not localStorage) is deliberate: the marker is scoped to the
 * current tab and cleared when the tab closes, so it can never leak into an
 * unrelated future session.
 */
export const CLUB_PELOTON_DEEPLINK_STORAGE_KEY = 'clubPeloton.deeplink.modal';

/**
 * The persisted marker is bounded by a short TTL so a marker written just
 * before login can only ever re-open the modal for the immediate post-login
 * navigation — never a stale visit minutes later.
 */
export const CLUB_PELOTON_DEEPLINK_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

type DeeplinkMarker = { ts: number };

/** True when the given search string requests the Club Peloton modal. */
export const hasClubPelotonModalParam = (search: string): boolean => {
  try {
    return new URLSearchParams(search).get(MODAL_PARAM) === CLUB_PELOTON_MODAL_NAME;
  } catch {
    return false;
  }
};

const safeStorage = (storage?: Storage): Storage | null => {
  if (storage) {
    return storage;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    // sessionStorage can throw when access is blocked (e.g. some privacy modes)
    return null;
  }
};

/**
 * Persist the pending Club Peloton modal deep-link intent when `search`
 * contains `?modal=clubPeloton`. No-op otherwise. Safe to call on every render
 * — writing is idempotent and swallows storage errors.
 */
export const captureClubPelotonDeeplinkIntent = (
  search: string,
  storage?: Storage,
): void => {
  if (!hasClubPelotonModalParam(search)) {
    return;
  }
  const store = safeStorage(storage);
  if (!store) {
    return;
  }
  try {
    const marker: DeeplinkMarker = { ts: Date.now() };
    store.setItem(CLUB_PELOTON_DEEPLINK_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // Best-effort: if the write fails the deep link simply relies on the URL
    // param, which is the pre-existing behaviour.
  }
};

/**
 * Read and clear any persisted deep-link marker (one-shot). Returns `true` only
 * when a *fresh* marker was present, signalling the modal should be re-opened.
 * Always clears the marker so it can never fire twice.
 */
export const consumeClubPelotonDeeplinkIntent = (
  storage?: Storage,
  now: number = Date.now(),
  maxAgeMs: number = CLUB_PELOTON_DEEPLINK_MAX_AGE_MS,
): boolean => {
  const store = safeStorage(storage);
  if (!store) {
    return false;
  }

  let raw: string | null = null;
  try {
    raw = store.getItem(CLUB_PELOTON_DEEPLINK_STORAGE_KEY);
    // One-shot: always remove, even if stale/malformed.
    store.removeItem(CLUB_PELOTON_DEEPLINK_STORAGE_KEY);
  } catch {
    return false;
  }

  if (!raw) {
    return false;
  }

  try {
    const marker = JSON.parse(raw) as Partial<DeeplinkMarker>;
    if (typeof marker.ts !== 'number') {
      return false;
    }
    return now - marker.ts <= maxAgeMs;
  } catch {
    return false;
  }
};
